import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Input,
  List,
  message,
  Modal,
  Popover,
  Row,
  Select,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import styled from "styled-components";
import useIsMobile from "../../../../hooks/useIsMobile";
import Loading from "../../../../components/shared-components/Loading";
import {
  DELIVERY_LABELS,
  ORDER_PICKING_STATUS_IN_PROGRESS,
  ORDER_STATUS_CANCELED,
  ORDER_STATUS_CREATED,
  ORDER_STATUS_PACKED,
} from "../../../../constants/OrderConstant";
import { ArrowLeftOutlined, ArrowRightOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useOrders } from "../../../../data/hooks/firestore/useOrders";
import { useCustomers } from "../../../../data/hooks/firestore/useCustomers";
import { useLeads } from "../../../../data/hooks/firestore/useLeads";
import { useOrderPickings } from "../../../../data/hooks/firestore/useOrderPickings";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { useCrateStocks } from "../../../../data/hooks/firestore/useCrateStocks";
import { useCountries } from "../../../../data/hooks/firestore/useCountries";
import { handleOrderPackingStatusChange } from "../../../../functions/orders";
import { checkOrderStockAvailability } from "../../../../functions/stockAvailability";
import { getPickingDate } from "../../../../functions/deliveryValidation";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { FIRESTORE_ORDERS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";

const { Title, Text } = Typography;

dayjs.extend(utc);
dayjs.extend(timezone);

// Widest lead time we currently need to look ahead for (24H/48H/72H delivery labels)
const MAX_LEAD_DAYS = 4;

const OrderPicking = ({ companyFilter = "All", title = "orderpicking", logoSrc, logoAlt }) => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const getTodayAmsterdam = () => dayjs().tz("Europe/Amsterdam").startOf("day");
  const [deliveryDate, setDeliveryDate] = useState(getTodayAmsterdam());
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("open");

  // deliveryDate here represents the *picking* date. Since a delivery label's lead
  // time can push the delivery date up to MAX_LEAD_DAYS after the picking date,
  // fetch a wider window and narrow it down to the picking date client-side below.
  const selectedDate = deliveryDate.format("YYYY-MM-DD");
  const queryEndDate = deliveryDate.add(MAX_LEAD_DAYS, "day").format("YYYY-MM-DD");
  const { data: orders, loading: ordersLoading } = useOrders(null, [selectedDate, queryEndDate]);
  // All customers incl. "new" (not yet accepted): their orders are also picked.
  const { allCustomers: customers, isLoading: customersLoading } = useCustomers();
  const { leads, isLoading: leadsLoading } = useLeads();
  const { products, isLoading: productsLoading } = useProducts();
  const { crateStocks, isLoading: crateStocksLoading } = useCrateStocks();
  const { getDeliveryLabelLeadDays } = useCountries();

  // Only get orderPickings for the selected delivery date
  const { orderPickings, loading: orderPickingsLoading } = useOrderPickings(selectedDate);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedDeliveryLabel, setSelectedDeliveryLabel] = useState("all");
  const [availableDeliveryLabels, setAvailableDeliveryLabels] = useState([]);
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const [stockPopoverVisible, setStockPopoverVisible] = useState({});

  const deliveryDateFormatted = deliveryDate.format("DD-MM-YYYY");

  // Load filters from localStorage on mount
  useEffect(() => {
    const savedSearchText = localStorage.getItem("orderPicking_searchText");
    const savedDeliveryLabel = localStorage.getItem("orderPicking_deliveryLabel");

    if (savedSearchText) {
      setSearchText(savedSearchText);
    }
    if (savedDeliveryLabel) {
      setSelectedDeliveryLabel(savedDeliveryLabel);
    }
    // deliveryDate (picking date) is always set to today by default (not loaded from localStorage)
  }, []);

  // Save searchText to localStorage
  useEffect(() => {
    localStorage.setItem("orderPicking_searchText", searchText);
  }, [searchText]);

  // Save selectedDeliveryLabel to localStorage
  useEffect(() => {
    localStorage.setItem("orderPicking_deliveryLabel", selectedDeliveryLabel);
  }, [selectedDeliveryLabel]);

  // deliveryDate (picking date) is NOT saved to localStorage - always defaults to today on page load

  useEffect(() => {
    if (
      orders &&
      customers &&
      leads &&
      !ordersLoading &&
      !customersLoading &&
      !leadsLoading &&
      !orderPickingsLoading &&
      !productsLoading &&
      !crateStocksLoading
    ) {
      setIsLoading(false);

      // Filter out canceled orders and apply company filter
      let filteredData = orders.filter((order) => order.farmorderStatus !== "canceled");

      // Keep only orders whose picking date (delivery date minus the lead time
      // configured for the country + delivery label) is the selected date.
      // Acquisition orders have no customer; their country comes from the lead.
      // For ADD orders, use the delivery label from the main order.
      filteredData = filteredData.filter((order) => {
        const country =
          order?.type === "acquisition"
            ? leads.find((l) => l.leadId === order.leadId)?.country
            : customers.find((c) => c.id === order.farmorderCustomerId)?.country;

        // For ADD orders, get delivery label from main order
        let deliveryLabel = order.deliveryLabel;
        if (deliveryLabel === "ADD" && order.mainOrderId) {
          const mainOrder = orders.find((o) => o.id === order.mainOrderId);
          if (mainOrder) {
            deliveryLabel = mainOrder.deliveryLabel;
          }
        }

        const leadDays = getDeliveryLabelLeadDays(country, deliveryLabel);
        const pickingDate = getPickingDate(order.farmorderDeliveryDate, leadDays);
        return pickingDate.format("YYYY-MM-DD") === selectedDate;
      });

      if (companyFilter !== "All") {
        filteredData = filteredData.filter((order) => {
          const customer = customers.find((c) => c.id === order.farmorderCustomerId);
          // Acquisition orders don't have a customer id but should always be treated as Chef's Farm.
          if (order?.type === "acquisition") {
            return companyFilter === "CF";
          }

          return customer?.company === companyFilter;
        });
      }

      // Extract unique delivery labels from filtered orders
      const uniqueLabels = [...new Set(filteredData.map((order) => order.deliveryLabel).filter(Boolean))];
      const availableLabels = DELIVERY_LABELS.filter((label) => uniqueLabels.includes(label.code));
      setAvailableDeliveryLabels(availableLabels);

      // Filter by search text (customer name or farmorderId)
      if (searchText) {
        filteredData = filteredData.filter((order) => {
          const customer = customers.find((c) => c.id === order.farmorderCustomerId);
          const leadCompanyName =
            order?.type === "acquisition" ? leads.find((l) => l.leadId === order.leadId)?.companyName : "";
          const customerName = leadCompanyName || customer?.customerName || order?.farmorderCustomerName || "";
          const customerNameMatch = customerName.toLowerCase().includes(searchText.toLowerCase());
          const orderIdMatch = order.farmorderId?.toString().toLowerCase().includes(searchText.toLowerCase());
          return customerNameMatch || orderIdMatch;
        });
      }

      // Filter by delivery label
      if (selectedDeliveryLabel !== "all") {
        filteredData = filteredData.filter((order) => order.deliveryLabel === selectedDeliveryLabel);
      }

      setFilteredOrders(filteredData);
    }
  }, [
    orders,
    customers,
    leads,
    companyFilter,
    searchText,
    selectedDeliveryLabel,
    selectedDate,
    getDeliveryLabelLeadDays,
    ordersLoading,
    customersLoading,
    leadsLoading,
    orderPickingsLoading,
    productsLoading,
    crateStocksLoading,
    products,
    crateStocks,
  ]);

  if (!isMobile) {
    navigate("/cms/orders/chefs-farm");
  }

  // Filter orders by packed status
  const openOrders = filteredOrders
    .filter((order) => order.packed === undefined || order.packed === false)
    .sort((a, b) => {
      // Sort by orderDateTime descending (newest first)
      // orderDateTime is a Unix timestamp
      const orderDateTimeA = a.orderDateTime || 0;
      const orderDateTimeB = b.orderDateTime || 0;
      return orderDateTimeB - orderDateTimeA;
    });
  const fulfilledOrders = filteredOrders
    .filter((order) => order.packed === true)
    .sort((a, b) => {
      // Sort by orderPicking completedAt descending (most recent first)
      // Look up the corresponding orderPicking by document ID
      const orderPickingA = orderPickings.find((op) => op.id === a.id);
      const orderPickingB = orderPickings.find((op) => op.id === b.id);

      const completedAtA = orderPickingA?.completedAt;
      const completedAtB = orderPickingB?.completedAt;

      // completedAt is a Unix timestamp
      if (!completedAtA && !completedAtB) return 0;
      if (!completedAtA) return 1;
      if (!completedAtB) return -1;
      return completedAtB - completedAtA;
    });
  const currentOrders = activeTab === "open" ? openOrders : fulfilledOrders;

  // Get remarks status (green/orange indicator)
  const getRemarksStatus = (order) => {
    if (!order.farmorderComments || order.farmorderComments === "" || order.orderCommentsProcessed === true) {
      return "success";
    }
    if (order.farmorderComments && !order.orderCommentsProcessed) {
      return "warning";
    }
    return "success";
  };

  // Check if order is currently being picked
  const getOrderPickingStatus = (orderId) => {
    if (!orderPickings) return null;

    const orderPickingDoc = orderPickings.find((picking) => picking.id === orderId);
    if (orderPickingDoc && orderPickingDoc.status === ORDER_PICKING_STATUS_IN_PROGRESS) {
      return {
        ...orderPickingDoc,
        isActivelyPicking: orderPickingDoc.isPaused === false,
        isPaused: orderPickingDoc.isPaused === true,
      };
    }
    return null;
  };

  const showActivePickingWarning = (userEmail, onConfirm) => {
    Modal.confirm({
      style: { maxWidth: "90%", marginTop: "40%" },
      title: t("order_being_actively_picked"),
      content: (
        <div>
          <p style={{ fontWeight: "bold", color: "#ff4d4f" }}>
            {t("order_currently_being_picked_by", { email: userEmail })}
          </p>
          <p>{t("active_picking_warning_concurrent")}</p>
        </div>
      ),
      okText: t("continue_anyway"),
      cancelText: t("cancel"),
      okButtonProps: { danger: true },
      onOk: onConfirm,
    });
  };

  // Handle order click to navigate to detail page
  const handleOrderClick = (order) => {
    const pickingStatus = getOrderPickingStatus(order.id);

    // Check if order is being actively picked
    if (pickingStatus && pickingStatus.isActivelyPicking) {
      showActivePickingWarning(pickingStatus.userEmail, () => {
        // User confirmed, proceed to detail page
        const companyType = companyFilter === "CF" ? "chefs-farm" : companyFilter === "GR" ? "growy" : "all";
        navigate(`/cms/orders/orderpicking/${companyType}/detail/${order.id}`);
      });
      return;
    }

    // If paused or not being picked, proceed normally
    const companyType = companyFilter === "CF" ? "chefs-farm" : companyFilter === "GR" ? "growy" : "all";
    navigate(`/cms/orders/orderpicking/${companyType}/detail/${order.id}`);
  };

  // Handle delivery date change
  const handleDeliveryDateChange = (date) => {
    setDeliveryDate(date ? dayjs(date).startOf("day") : getTodayAmsterdam());
  };

  const handlePackedStatusChange = async (orderId, packed) => {
    try {
      const order = orders.find((o) => o.id === orderId);
      if (!order) {
        message.error(t("notifications.order_not_found"));
        return;
      }

      const result = await handleOrderPackingStatusChange(order, packed, { updatedBy: email });
      if (!result.success) {
        message.error(
          t(
            `notifications.${
              result.error.includes("invoice") ? "cannot_unpack_completed_invoice" : "error_updating_packed_status"
            }`,
          ),
        );
        return;
      }

      // Update the packed status (and invoiceId) in Firestore
      await updateDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, orderId),
        result.updateObject,
      );

      // Notify after Firestore update
      if (packed) {
        message.success(t("notifications.order_packed_invoice_created"));
      } else {
        message.success(t("notifications.order_unpacked_invoice_deleted"));
      }

      // Force table to re-sort by updating local state
      setFilteredOrders((prev) =>
        prev
          .map((order) =>
            order.id === orderId
              ? {
                  ...order,
                  packed,
                  invoiceId: packed ? result.invoiceId : null,
                  farmorderStatus: packed ? ORDER_STATUS_PACKED : ORDER_STATUS_CREATED,
                  farmorderId: result.updateObject.farmorderId || order.farmorderId,
                }
              : order,
          )
          .sort((a, b) => {
            const aPacked = a.packed || false;
            const bPacked = b.packed || false;
            return aPacked === bPacked ? 0 : aPacked ? 1 : -1;
          }),
      );
    } catch (error) {
      console.error("Error updating packed status:", error);
      message.error(t("notifications.error_updating_packed_status"));
    }
  };

  // Confirm unpack action to avoid accidental clicks
  const confirmUnpack = (order) => {
    Modal.confirm({
      title: t("confirm_unpack_title"),
      content: t("confirm_unpack_message"),
      okText: t("yes_unpack"),
      cancelText: t("cancel"),
      onOk: () => handlePackedStatusChange(order.id, false),
    });
  };

  const renderOrderItem = (order) => {
    const customer = customers.find((c) => c.id === order.farmorderCustomerId);
    const leadCompanyName =
      order?.type === "acquisition" ? leads.find((l) => l.leadId === order.leadId)?.companyName : null;
    const remarksStatus = getRemarksStatus(order);
    const pickingStatus = getOrderPickingStatus(order.id);
    const stockAvailability = checkOrderStockAvailability(order, products, crateStocks);

    return (
      <StyledListItem
        onClick={() => handleOrderClick(order)}
        $isPacked={order.packed}
        $isActivelyPicking={pickingStatus?.isActivelyPicking}
        $isPaused={pickingStatus?.isPaused}
      >
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <div style={{ marginBottom: "2px" }}>
            <div>
              {!!order.farmorderComments && (
                <Badge status={remarksStatus} style={{ fontSize: "12px", marginRight: 8 }} />
              )}
              <Text strong style={{ fontSize: "16px" }}>
                {leadCompanyName || customer?.customerName || order?.farmorderCustomerName || "Unknown Customer"}
              </Text>
              <br />
              <Text type="secondary">#{order.farmorderId}</Text>
              {pickingStatus && (
                <div style={{ marginTop: "4px" }}>
                  <Badge status={pickingStatus.isActivelyPicking ? "processing" : "default"} />
                  <Text
                    style={{
                      fontSize: "12px",
                      color: pickingStatus.isActivelyPicking ? "#faad14" : "#1890ff",
                      marginLeft: 5,
                    }}
                  >
                    {pickingStatus.isActivelyPicking ? t("being_picked_by") : t("paused_by")} {pickingStatus.userEmail}
                  </Text>
                </div>
              )}
              <div style={{ marginTop: "4px" }}>
                {!stockAvailability.available ? (
                  <Popover
                    content={
                      <div style={{ maxWidth: 300 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 12,
                          }}
                        >
                          <Text strong>{t("insufficient_stock_details")}</Text>
                          <Button
                            type="text"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setStockPopoverVisible({ ...stockPopoverVisible, [order.id]: false });
                            }}
                            style={{ padding: 0, minWidth: 20 }}
                          >
                            ✕
                          </Button>
                        </div>
                        <div style={{ maxHeight: 300, overflowY: "auto" }}>
                          {stockAvailability.missingProducts.map((item, index) => (
                            <div
                              key={index}
                              style={{
                                padding: "12px",
                                marginBottom: index < stockAvailability.missingProducts.length - 1 ? "8px" : "0",
                                backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                                borderRadius: "4px",
                              }}
                            >
                              <Text strong style={{ display: "block", marginBottom: 4 }}>
                                {item.productName}
                              </Text>
                              {item.isBundleItself && (
                                <Text
                                  type="secondary"
                                  style={{ display: "block", fontSize: 11, marginBottom: 8, fontStyle: "italic" }}
                                >
                                  {t("bundle_itself")}
                                </Text>
                              )}
                              {item.isBundle && !item.isBundleItself && (
                                <div style={{ marginBottom: 8 }}>
                                  <Text
                                    type="secondary"
                                    style={{ display: "block", fontSize: 11, fontStyle: "italic" }}
                                  >
                                    {t("from_bundle")}: {item.bundleName}
                                  </Text>
                                  <Text
                                    type="secondary"
                                    style={{ display: "block", fontSize: 11, fontStyle: "italic" }}
                                  >
                                    {t("or_add_bundles", { count: item.missingBundleQty })}
                                  </Text>
                                </div>
                              )}
                              <div style={{ fontSize: 12 }}>
                                <div style={{ marginBottom: 2 }}>
                                  <Text type="secondary">{t("required")}:</Text> <Text>{item.required}</Text>
                                </div>
                                <div style={{ marginBottom: 2 }}>
                                  <Text type="secondary">{t("available")}:</Text> <Text>{item.available}</Text>
                                </div>
                                <div>
                                  <Text type="danger">{t("missing")}:</Text>{" "}
                                  <Text strong style={{ color: "#ff4d4f" }}>
                                    {item.missing}
                                  </Text>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <Button
                          size="small"
                          block
                          onClick={(e) => {
                            e.stopPropagation();
                            setStockPopoverVisible({ ...stockPopoverVisible, [order.id]: false });
                          }}
                          style={{ marginTop: 12 }}
                        >
                          {t("close")}
                        </Button>
                      </div>
                    }
                    title={null}
                    trigger="click"
                    open={stockPopoverVisible[order.id]}
                    onOpenChange={(visible) => {
                      setStockPopoverVisible({ ...stockPopoverVisible, [order.id]: visible });
                    }}
                    placement="bottomLeft"
                  >
                    <span
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                    >
                      <Badge status="error" />
                      <Text
                        style={{
                          fontSize: "12px",
                          color: "#ff4d4f",
                          marginLeft: 5,
                        }}
                      >
                        {t("insufficient_stock")} ({stockAvailability.missingProducts.length})
                      </Text>
                    </span>
                  </Popover>
                ) : (
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    <Badge status="success" />
                    <Text
                      style={{
                        fontSize: "12px",
                        color: "#52c41a",
                        marginLeft: 5,
                      }}
                    >
                      {t("stock_available")}
                    </Text>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "space-between",
                height: "100%",
              }}
            >
              {order.packed && (
                <Checkbox
                  checked={order.packed || false}
                  disabled={order.invoiceStatus === "in_exact" || order.farmorderStatus === ORDER_STATUS_CANCELED}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    const checked = e.target.checked;
                    if (!checked) {
                      // Confirm before unpacking
                      confirmUnpack(order);
                    } else {
                      // In case packing from here is ever enabled
                      handlePackedStatusChange(order.id, true);
                    }
                  }}
                  style={{ transform: "scale(1.5)" }}
                />
              )}
              <Text style={{ fontSize: "12px", minWidth: 40 }}>{order.deliveryLabel || "No Label"}</Text>
            </div>
          </div>
        </div>
      </StyledListItem>
    );
  };

  if (
    isLoading ||
    ordersLoading ||
    customersLoading ||
    leadsLoading ||
    orderPickingsLoading ||
    productsLoading ||
    crateStocksLoading
  ) {
    return <Loading />;
  }

  const displayDate = deliveryDate ? deliveryDate.format("DD-MM-YYYY") : "";
  const isSelectedDateNotToday =
    !!deliveryDate && deliveryDate.format("YYYY-MM-DD") !== getTodayAmsterdam().format("YYYY-MM-DD");

  return (
    <div style={{ minHeight: "100vh" }}>
      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12}>
          <Card>
            {/* Header with logo and title */}
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <img src={logoSrc} alt={logoAlt} style={{ height: "40px", marginBottom: "16px" }} />
              <Title level={3} style={{ margin: 0 }}>
                {t(title)}
              </Title>
            </div>

            <p style={{ textAlign: "center", margin: 0, color: "#1a3353" }}>{t("picking_date")}:</p>

            {/* Picking date selector with arrows and direct datepicker */}
            <div
              style={{
                textAlign: "center",
                marginBottom: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => handleDeliveryDateChange(deliveryDate.subtract(1, "day").startOf("day"))}
              />

              <div>{displayDate}</div>

              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => handleDeliveryDateChange(deliveryDate.add(1, "day").startOf("day"))}
              />
            </div>

            {isSelectedDateNotToday && (
              <div style={{ textAlign: "center", marginBottom: "12px" }}>
                <Text style={{ color: "#ff4d4f", fontWeight: 600 }}>{t("selected_date_not_today")}</Text>
              </div>
            )}

            {/* Tab buttons */}
            <div style={{ display: "flex", marginBottom: "16px" }}>
              <Button
                type={activeTab === "open" ? "primary" : "default"}
                onClick={() => setActiveTab("open")}
                style={{ flex: 1, marginRight: "8px" }}
              >
                {t("open")} ({openOrders.length})
              </Button>
              <Button
                type={activeTab === "packed" ? "primary" : "default"}
                onClick={() => setActiveTab("packed")}
                style={{ flex: 1 }}
              >
                {t("packed")} ({fulfilledOrders.length})
              </Button>

              {/* Filter button with popover */}
              <FilterContainer style={{ marginLeft: 8 }}>
                <Popover
                  content={
                    <FilterPopoverContent>
                      <div>
                        <FilterLabel>{t("search_company_name")}</FilterLabel>
                        <Input
                          placeholder={t("search_company_name")}
                          prefix={<SearchOutlined />}
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          allowClear
                          size="large"
                        />
                      </div>
                      <div>
                        <FilterLabel>{t("shipment_type")}</FilterLabel>
                        <Select
                          value={selectedDeliveryLabel}
                          onChange={setSelectedDeliveryLabel}
                          style={{ width: "100%" }}
                          size="large"
                        >
                          <Select.Option value="all">{t("all_shipment_types")}</Select.Option>
                          {availableDeliveryLabels.map((label) => (
                            <Select.Option key={label.code} value={label.code}>
                              {label.code}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    </FilterPopoverContent>
                  }
                  title={t("filter_orders")}
                  trigger="click"
                  open={filterPopoverVisible}
                  onOpenChange={setFilterPopoverVisible}
                  placement="bottomRight"
                >
                  <Button
                    icon={<FilterOutlined />}
                    style={{
                      borderColor: searchText || selectedDeliveryLabel !== "all" ? "#FF4B33" : "",
                      color: searchText || selectedDeliveryLabel !== "all" ? "#FF4B33" : "",
                    }}
                  />
                </Popover>
              </FilterContainer>
            </div>

            {/* Orders list */}
            <List
              dataSource={currentOrders}
              renderItem={renderOrderItem}
              locale={{
                emptyText: activeTab === "open" ? t("no_open_orders") : t("no_fulfilled_orders"),
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// Styled Components
const StyledListItem = styled(List.Item)`
  padding: 16px !important;
  border: 1px solid #f0f0f0 !important;
  border-radius: 8px;
  margin-bottom: 8px;
  background-color: ${(props) =>
    props.$isPacked ? "#fafafa" : props.$isActivelyPicking ? "#fff7e6" : props.$isPaused ? "#e6f7ff" : "#fff"};
  cursor: pointer;
  position: relative;
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
`;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

export default OrderPicking;
